import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import PageStickyChrome from '../../components/PageStickyChrome/PageStickyChrome';
import { ROLE } from '../../enum/role';
import { ACTIVE_DEVICE } from '../../common/constant/common-constants';
import {
  DEFAULT_DEVICE_MODELS,
  PROCESS_POLL_MS,
  assignManualToMachine,
  fetchDeviceDocuments,
  fetchDocumentAssignments,
  getAssignmentModel,
  getAssignmentSerial,
  getDeviceDocumentsErrorMessage,
  getDeviceDocumentsErrorStatus,
  processUserManual,
  removeManualAssignment,
  setDocumentStatus,
  uploadUserManual,
} from '../../helper/DeviceDocumentsHelper';
import ConfirmModal from './ConfirmModal';
import UploadManualModal from './UploadManualModal';
import AssignVersionModal from './AssignVersionModal';
import UserManualsPanel from './UserManualsPanel';
import MachineAssignmentsPanel from './MachineAssignmentsPanel';
import '../HospitalDashboard/HospitalDashboard.css';
import './DeviceDocuments.css';

const TABS = {
  MANUALS: 'manuals',
  ASSIGNMENTS: 'assignments',
};

const defaultToast = { open: false, tone: 'success', title: '', message: '' };

const DeviceDocuments = ({ userRole }) => {
  const allowed = userRole === ROLE.ADMIN || userRole === ROLE.SERVICE;
  const [tab, setTab] = useState(TABS.ASSIGNMENTS);
  const [documents, setDocuments] = useState([]);
  const [models, setModels] = useState(DEFAULT_DEVICE_MODELS);
  const [selectedModel, setSelectedModel] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [processingIds, setProcessingIds] = useState([]);
  const [highlightedId, setHighlightedId] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignPrefill, setAssignPrefill] = useState({});
  const [assignBusy, setAssignBusy] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(defaultToast);
  const toastTimer = useRef(null);

  const processingIdSet = useMemo(() => new Set(processingIds), [processingIds]);

  const showToast = useCallback((next) => {
    setToast({ open: true, tone: 'success', ...next });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(defaultToast), 7000);
  }, []);

  const loadDocuments = useCallback((options = {}) => {
    const silent = Boolean(options.silent);
    if (!silent) setLoadingDocs(true);
    return fetchDeviceDocuments()
      .then((data) => {
        const list = Array.isArray(data?.documents) ? data.documents : Array.isArray(data) ? data : [];
        setDocuments(list);
        const nextModels = Array.isArray(data?.supportedModels) && data.supportedModels.length
          ? data.supportedModels
          : DEFAULT_DEVICE_MODELS;
        setModels(nextModels);
        setProcessingIds((ids) => ids.filter((id) => {
          const doc = list.find((item) => item.id === id);
          if (!doc) return true;
          const status = String(doc.processingStatus || '').toLowerCase();
          return status === 'pending' || status === 'processing';
        }));
        return list;
      })
      .catch((err) => {
        showToast({
          tone: 'error',
          title: 'Could not load manuals',
          message: getDeviceDocumentsErrorMessage(err),
        });
        return [];
      })
      .finally(() => {
        if (!silent) setLoadingDocs(false);
      });
  }, [showToast]);

  const loadAssignments = useCallback(() => {
    setLoadingAssignments(true);
    return fetchDocumentAssignments()
      .then((data) => {
        const records = Array.isArray(data?.records) ? data.records : Array.isArray(data) ? data : [];
        setAssignments(records);
        return records;
      })
      .catch((err) => {
        const status = getDeviceDocumentsErrorStatus(err);
        if (status && status !== 404) {
          showToast({
            tone: 'error',
            title: 'Could not load assignments',
            message: getDeviceDocumentsErrorMessage(err),
          });
        }
        setAssignments([]);
        return [];
      })
      .finally(() => setLoadingAssignments(false));
  }, [showToast]);

  useEffect(() => {
    if (!allowed) return undefined;
    loadDocuments();
    return () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    };
  }, [allowed, loadDocuments]);

  useEffect(() => {
    if (!allowed || tab !== TABS.ASSIGNMENTS) return;
    loadAssignments();
  }, [allowed, tab, loadAssignments]);

  useEffect(() => {
    const fromList = documents
      .filter((doc) => String(doc.processingStatus || '').toLowerCase() === 'processing')
      .map((doc) => doc.id);
    const watching = new Set([...processingIds, ...fromList]);
    if (watching.size === 0) return undefined;
    const timer = window.setInterval(() => {
      loadDocuments({ silent: true });
    }, PROCESS_POLL_MS);
    return () => window.clearInterval(timer);
  }, [documents, processingIds, loadDocuments]);

  const startProcess = (doc) => {
    const documentId = doc.id || doc;
    setProcessingIds((ids) => (ids.includes(documentId) ? ids : [...ids, documentId]));
    processUserManual(documentId)
      .then(() => {
        showToast({
          title: 'Processing started',
          message: 'Pages are being generated. This table will refresh automatically.',
        });
        loadDocuments({ silent: true });
      })
      .catch((err) => {
        setProcessingIds((ids) => ids.filter((id) => id !== documentId));
        showToast({
          tone: 'error',
          title: 'Processing failed to start',
          message: getDeviceDocumentsErrorMessage(err),
        });
      });
  };

  const handleUpload = (payload) => {
    setUploadBusy(true);
    uploadUserManual(payload)
      .then((data) => {
        const documentId = data?.documentId;
        setUploadOpen(false);
        setSelectedModel(payload.deviceModel || '');
        if (documentId) {
          setHighlightedId(documentId);
          startProcess({ id: documentId });
        }
        showToast({
          title: 'Manual uploaded',
          message: documentId
            ? `Document #${documentId} is processing. It will not become the default until you set it.`
            : data?.message || 'Processing will generate pages next.',
        });
        loadDocuments({ silent: true });
      })
      .catch((err) => {
        const status = getDeviceDocumentsErrorStatus(err);
        if (status === 409) {
          setUploadOpen(false);
          const version = String(payload.version || '').trim();
          loadDocuments().then((list) => {
            const existing = list.find((doc) =>
              doc.deviceModel === payload.deviceModel && String(doc.version) === version
            );
            if (existing) {
              setHighlightedId(existing.id);
              setSelectedModel(payload.deviceModel || '');
            }
          });
          showToast({
            tone: 'error',
            title: 'Version already exists',
            message: 'This product already has that version. The existing row is highlighted.',
          });
          return;
        }
        showToast({
          tone: 'error',
          title: 'Upload failed',
          message: getDeviceDocumentsErrorMessage(err, 'Missing PDF, invalid file, or missing fields.'),
        });
      })
      .finally(() => setUploadBusy(false));
  };

  const handleSetDefault = (doc) => {
    setConfirm({
      title: 'Set as default',
      message: `Unassigned ${doc.deviceModel} machines will open this version. Machines with a mapping keep their mapped document.`,
      confirmLabel: 'Set as default',
      onConfirm: () => {
        setConfirm((prev) => ({ ...prev, busy: true }));
        setDocumentStatus(doc.id, 'active')
          .then(() => {
            setConfirm(null);
            showToast({
              title: 'Default updated',
              message: `${doc.deviceModel} ${doc.version} is now the product default.`,
            });
            loadDocuments({ silent: true });
          })
          .catch((err) => {
            setConfirm(null);
            showToast({
              tone: 'error',
              title: 'Could not set default',
              message: getDeviceDocumentsErrorMessage(err),
            });
          });
      },
    });
  };

  const handleRemoveDefault = (doc) => {
    setConfirm({
      title: 'Remove default',
      message: `Unassigned ${doc.deviceModel} machines will have no published default until another version is activated.`,
      confirmLabel: 'Remove default',
      confirmTone: 'danger',
      onConfirm: () => {
        setConfirm((prev) => ({ ...prev, busy: true }));
        setDocumentStatus(doc.id, 'inactive')
          .then(() => {
            setConfirm(null);
            showToast({
              title: 'Default removed',
              message: `${doc.deviceModel} ${doc.version} is no longer the product default.`,
            });
            loadDocuments({ silent: true });
          })
          .catch((err) => {
            setConfirm(null);
            showToast({
              tone: 'error',
              title: 'Could not remove default',
              message: getDeviceDocumentsErrorMessage(err),
            });
          });
      },
    });
  };

  const openAssignModal = (prefill = {}) => {
    setAssignPrefill(prefill);
    setAssignOpen(true);
  };

  const handleAssignOne = (machineUDI, documentId) => {
    setAssignBusy(true);
    assignManualToMachine({ machineUDI, documentId })
      .then((data) => {
        setAssignOpen(false);
        setAssignPrefill({});
        showToast({
          title: 'Document mapped',
          message: `${data?.machineUDI || machineUDI} is mapped to ${data?.version || 'this version'}.`,
        });
        loadAssignments();
      })
      .catch((err) => {
        const status = getDeviceDocumentsErrorStatus(err);
        const fallback = status === 404
          ? 'Machine number or document was not found.'
          : 'Document is not ready, or the product does not match the machine.';
        showToast({
          tone: 'error',
          title: 'Mapping failed',
          message: getDeviceDocumentsErrorMessage(err, fallback),
        });
      })
      .finally(() => setAssignBusy(false));
  };

  const handleUnassign = (machineUDI) => {
    setConfirm({
      title: 'Remove mapping',
      message: 'QR will use the product default for this machine number.',
      confirmLabel: 'Remove mapping',
      confirmTone: 'danger',
      onConfirm: () => {
        setConfirm((prev) => ({ ...prev, busy: true }));
        removeManualAssignment({ machineUDI })
          .then(() => {
            setConfirm(null);
            showToast({
              title: 'Mapping removed',
              message: `${machineUDI} now uses the product default.`,
            });
            loadAssignments();
          })
          .catch((err) => {
            setConfirm(null);
            showToast({
              tone: 'error',
              title: 'Could not remove mapping',
              message: getDeviceDocumentsErrorMessage(err),
            });
          });
      },
    });
  };

  const handleAssignFromTable = (doc) => {
    setTab(TABS.ASSIGNMENTS);
    openAssignModal({
      documentId: doc.id,
      deviceModel: doc.deviceModel || '',
    });
  };

  if (!allowed) {
    return (
      <div>
        <h1>Unauthorized Access</h1>
        <p>You do not have the necessary permissions to access this page.</p>
      </div>
    );
  }

  return (
    <div className="docs-page">
      {toast.open ? (
        <div className={`docs-toast${toast.tone === 'error' ? ' docs-toast--error' : ''}`} role="status" aria-live="polite">
          <span className="docs-toast-icon" aria-hidden="true">{toast.tone === 'error' ? '!' : '✓'}</span>
          <div className="docs-toast-text">
            <strong>{toast.title}</strong>
            <span>{toast.message}</span>
          </div>
        </div>
      ) : null}

      <PageStickyChrome>
        <div className="docs-header">
          <div>
            <h3 className="docs-title">Documents</h3>
            <p className="docs-subtitle">
              Assign a document version to a specific machine number, or upload versions and set the product default. QR uses the assigned document if present, otherwise the default.
            </p>
          </div>
          <div className="docs-tabs" role="tablist" aria-label="Document screens">
            <button
              type="button"
              className={`docs-tab${tab === TABS.ASSIGNMENTS ? ' docs-tab--active' : ''}`}
              onClick={() => setTab(TABS.ASSIGNMENTS)}
            >
              Machine document version
            </button>
            <button
              type="button"
              className={`docs-tab${tab === TABS.MANUALS ? ' docs-tab--active' : ''}`}
              onClick={() => setTab(TABS.MANUALS)}
            >
              User manuals
            </button>
          </div>
        </div>
      </PageStickyChrome>

      {tab === TABS.ASSIGNMENTS ? (
        <MachineAssignmentsPanel
          assignments={assignments}
          loadingAssignments={loadingAssignments}
          onAssignClick={() => openAssignModal()}
          onChangeClick={(row) => openAssignModal({
            machineUDI: getAssignmentSerial(row),
            documentId: row.documentId,
            deviceModel: getAssignmentModel(row),
          })}
          onUnassign={handleUnassign}
        />
      ) : (
        <UserManualsPanel
          documents={documents}
          models={models}
          selectedModel={selectedModel}
          onModelChange={setSelectedModel}
          highlightedId={highlightedId}
          loading={loadingDocs}
          onUploadClick={() => setUploadOpen(true)}
          onProcess={startProcess}
          onSetDefault={handleSetDefault}
          onRemoveDefault={handleRemoveDefault}
          onAssign={handleAssignFromTable}
          processingIds={processingIdSet}
        />
      )}

      {uploadOpen ? (
        <UploadManualModal
          models={models}
          defaultModel={selectedModel || ACTIVE_DEVICE.V730i}
          busy={uploadBusy}
          onClose={() => !uploadBusy && setUploadOpen(false)}
          onSubmit={handleUpload}
        />
      ) : null}

      {assignOpen ? (
        <AssignVersionModal
          documents={documents}
          models={models}
          busy={assignBusy}
          defaultSerial={assignPrefill.machineUDI || ''}
          defaultDocumentId={assignPrefill.documentId || ''}
          defaultModel={assignPrefill.deviceModel || ''}
          onClose={() => !assignBusy && setAssignOpen(false)}
          onSubmit={handleAssignOne}
        />
      ) : null}

      {confirm ? (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          confirmTone={confirm.confirmTone}
          busy={Boolean(confirm.busy)}
          onClose={() => !confirm.busy && setConfirm(null)}
          onConfirm={confirm.onConfirm}
        />
      ) : null}
    </div>
  );
};

const mapStateToProps = (state) => ({
  userRole: state.userDetails.userRole,
});

export default connect(mapStateToProps)(DeviceDocuments);
